/**
 * Bande « Aujourd'hui » (specs/01 §2) : matchs dont la date LOCALE du visiteur
 * est aujourd'hui — l'élément le plus visible de la page.
 *
 * Carte ALLÉGÉE : le score est porté PAR ÉQUIPE (lisible), les buteurs sont
 * indentés sous l'équipe qui a marqué (« Joueur 32′ », sans drapeau ni
 * répétition), la mi-temps n'est plus ici (gardée dans la carte « En direct »).
 */
import type { MatchView, OfficialState } from "../../app/officialStore.ts";
import { fmtTime, isInTodayBand, localDayKey, fmtDayLabel } from "../../app/format.ts";
import { t, tp, localeTag } from "../../app/i18n.ts";
import { esc, flagSpan, roundName } from "../html.ts";

const DAY_MS = 86_400_000;
/** Écart en jours entre deux clés "YYYY-MM-DD". */
function dayDiff(a: string, b: string): number {
  return Math.round((Date.parse(a) - Date.parse(b)) / DAY_MS);
}

/** Drapeau + nom, ou jeton symbolique pour un slot KO non résolu. */
function teamCell(code: string): string {
  return /^[A-Z]{3}$/.test(code)
    ? flagSpan(code, { name: true })
    : `<span class="ph"></span><span class="cd dim">${esc(code)}</span>`;
}

/** Code résolu d'un côté : pour un match KO, l'équipe du bracket (state.bracket[Mxx].a/b)
 *  plutôt que le jeton du calendrier (« 1F », « 3:CDFGH »). Sinon le code du calendrier. */
function sideCode(state: OfficialState, v: MatchView, side: "home" | "away"): string {
  const schedule = side === "home" ? v.match.home : v.match.away;
  if (v.match.stage === "group") return schedule;
  const node = state.bracket["M" + String(v.match.id)];
  const resolved = node ? (side === "home" ? node.a : node.b) : null;
  return resolved ?? schedule;
}

/** Ligne d'équipe : drapeau + nom + son nombre de buts (rien si non commencé). */
function teamLine(state: OfficialState, v: MatchView, side: "home" | "away"): string {
  const code = sideCode(state, v, side);
  const live = v.status === "live" || v.status === "paused";
  const ft = v.displayScore ?? v.live?.score?.ft ?? null;
  const goals = ft && (v.status === "finished" || live) ? ft[side === "home" ? 0 : 1] : null;
  const win = v.status === "finished" && v.penalties?.winner === side;
  const pts = goals !== null ? `<span class="tc-pts ${win ? "win" : ""}">${goals}</span>` : "";
  return `<div class="tc-team">${teamCell(code)}${pts}</div>`;
}

function cardHtml(state: OfficialState, v: MatchView): string {
  const head =
    v.match.stage === "group"
      ? t("lf.groupMatchdayAbbr", { group: v.match.group ?? "", matchday: v.match.matchday ?? "" })
      : roundName(v.match.stage);
  const live = v.status === "live" || v.status === "paused";
  const minute = v.status === "paused" ? t("common.halfTimeAbbr") : v.minute != null ? `${v.minute}′` : "live";
  const liveBadge = live ? `<span class="badge-live">${esc(minute)}</span>` : "";
  const pens =
    v.status === "finished" && v.penalties
      ? `<div class="tc-pens dim">${t("common.penaltiesAbbr")} ${v.penalties.score[0]}–${v.penalties.score[1]}</div>`
      : "";
  const statusLine =
    v.status === "scheduled"
      ? `<div class="tc-time">${esc(fmtTime(v.match.utcDateTime))}</div>`
      : v.status === "postponed"
        ? `<div class="tc-time dim">${esc(t("common.postponed"))}</div>`
        : "";
  return `
    <article class="today-card ${v.status}" role="listitem" data-match="${v.match.id}">
      <div class="tc-head"><span>${esc(head)}</span>${liveBadge}</div>
      ${teamLine(state, v, "home")}
      ${teamLine(state, v, "away")}
      ${pens}
      ${statusLine}
      <div class="tc-venue">${esc(v.match.city ?? "")}</div>
    </article>`;
}

/** Dernier `dayOffset` rendu : on préserve le défilement horizontal entre deux
 *  rendus du MÊME jour (les ticks live re-rendent la bande) et on repart à 0
 *  quand on change de jour. */
let lastBandOffset: number | null = null;

/**
 * Branche l'indice « on peut faire défiler » : le chevron animé + le fondu de
 * droite n'apparaissent QUE quand la bande déborde et qu'on n'est pas au bout.
 * Réattaché à chaque rendu (l'ancien écouteur part avec l'ancien nœud DOM).
 */
function wireScrollHint(el: HTMLElement, restoreScroll: number): void {
  const wrap = el.querySelector<HTMLElement>(".today-scroll-wrap");
  const scroller = el.querySelector<HTMLElement>(".today-scroll");
  if (!wrap || !scroller) return;
  scroller.scrollLeft = restoreScroll;
  const update = (): void => {
    const overflow = scroller.scrollWidth - scroller.clientWidth > 8;
    const atEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 8;
    wrap.classList.toggle("more", overflow && !atEnd);
  };
  scroller.addEventListener("scroll", update, { passive: true });
  update();
  // re-mesure après layout/paint (largeurs prêtes même si la section vient
  // d'être ré-affichée) — sinon l'indice peut manquer au tout premier rendu.
  requestAnimationFrame(update);
}

/** Matchs d'un jour donné (offset relatif à aujourd'hui). off=0 = la bande
 *  « Aujourd'hui » (inclut la nuit jusqu'à 9h) ; sinon le jour calendaire exact. */
function matchesForOffset(state: OfficialState, nowIso: string, off: number): MatchView[] {
  const list =
    off === 0
      ? state.matches.filter((v) => isInTodayBand(v.match.utcDateTime, nowIso))
      : (() => {
          const key = localDayKey(new Date(Date.parse(nowIso) + off * DAY_MS).toISOString());
          return state.matches.filter((v) => localDayKey(v.match.utcDateTime) === key);
        })();
  return [...list].sort((a, b) => a.match.utcDateTime.localeCompare(b.match.utcDateTime));
}

/** Libellé compact d'un onglet de jour : « Auj. » pour aujourd'hui, sinon jour
 *  abrégé + date (« sam » / « 14/06 »). */
function dayTabLabel(nowIso: string, off: number): { top: string; bot: string } {
  const d = new Date(Date.parse(nowIso) + off * DAY_MS);
  return {
    top:
      off === 0
        ? t("today.short")
        : d.toLocaleDateString(localeTag(), { weekday: "short" }).replace(".", ""),
    bot: d.toLocaleDateString(localeTag(), { day: "2-digit", month: "2-digit" }),
  };
}

/**
 * Bande des matchs, NAVIGABLE par ONGLETS de date (aujourd'hui au centre, ouvert).
 * `dayOffset` = 0 → « Aujourd'hui » (inclut la nuit jusqu'à 9h, décision 13/06/2026) ;
 * négatif → un jour passé ; positif → un jour à venir. Un onglet par jour ayant des
 * matchs (+ aujourd'hui toujours) ; clic → matchs de ce jour (stats au survol).
 */
export function renderTodayBand(
  el: HTMLElement,
  state: OfficialState,
  nowIso: string,
  dayOffset = 0,
): void {
  const todayKey = localDayKey(nowIso);
  let minOff = 0;
  let maxOff = 0;
  for (const v of state.matches) {
    const d = dayDiff(localDayKey(v.match.utcDateTime), todayKey);
    if (d < minOff) minOff = d;
    if (d > maxOff) maxOff = d;
  }
  const off = Math.max(minOff, Math.min(maxOff, dayOffset));

  const list = matchesForOffset(state, nowIso, off);
  const label =
    off === 0 ? t("today") : fmtDayLabel(new Date(Date.parse(nowIso) + off * DAY_MS).toISOString());
  const count =
    list.length > 0
      ? `${tp("today.match", list.length)}${off === 0 ? ` · ${t("today.untilTomorrow")}` : ""}`
      : t("today.noMatch");

  // Un onglet par jour AYANT des matchs (+ aujourd'hui toujours présent).
  const dayOffs = new Set<number>([0]);
  for (const v of state.matches) dayOffs.add(dayDiff(localDayKey(v.match.utcDateTime), todayKey));
  const offs = [...dayOffs].filter((o) => o >= minOff && o <= maxOff).sort((a, b) => a - b);
  const tabs = offs
    .map((o) => {
      const n = matchesForOffset(state, nowIso, o).length;
      const { top, bot } = dayTabLabel(nowIso, o);
      const on = o === off;
      return `<button type="button" class="day-tab${on ? " on" : ""}${o === 0 ? " is-today" : ""}" data-offset="${o}" role="tab" aria-selected="${on}" aria-label="${esc(top)} ${esc(bot)} — ${esc(tp("today.match", n))}">
        <span class="dt-top">${esc(top)}</span>
        <span class="dt-bot">${esc(bot)}</span>
        <span class="dt-n${n ? "" : " dt-n0"}">${n || "·"}</span>
      </button>`;
    })
    .join("");
  const nav = `
    <h2 class="sec-title day-label">${esc(label)} <span class="count">${esc(count)}</span></h2>
    <div class="day-tabs-wrap"><div class="day-tabs" role="tablist" aria-label="${t("today.choose")}">${tabs}</div></div>`;
  const body =
    list.length > 0
      ? `<div class="today-scroll-wrap">
           <div class="today-scroll" role="list" tabindex="0" aria-label="${t("today.scrollAria")}">${list.map((v) => cardHtml(state, v)).join("")}</div>
           <div class="today-hint" aria-hidden="true"><i class="ti ti-chevron-right"></i></div>
         </div>`
      : `<div class="today-empty note">${t("today.none")}</div>`;
  // Préserve le défilement de la bande de MATCHS entre deux rendus du même jour.
  const prevScroll =
    lastBandOffset === off ? (el.querySelector<HTMLElement>(".today-scroll")?.scrollLeft ?? 0) : 0;
  lastBandOffset = off;
  el.innerHTML = nav + body;
  wireScrollHint(el, prevScroll);
  // L'onglet ACTIF (aujourd'hui par défaut) est CENTRÉ dans le strip à chaque rendu.
  // Lecture SYNCHRONE des dimensions après innerHTML (force le layout → valeurs
  // justes, pas de course rAF) ; la section est déjà visible quand on rend ici.
  const tabsRow = el.querySelector<HTMLElement>(".day-tabs");
  const activeTab = el.querySelector<HTMLElement>(".day-tab.on");
  if (tabsRow && activeTab && tabsRow.clientWidth > 0) {
    tabsRow.scrollLeft = activeTab.offsetLeft - (tabsRow.clientWidth - activeTab.clientWidth) / 2;
  }
}
